import { passwordProblem, PASSWORD_MIN } from '@api/auth/password-rules';
import { KeyRound } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { ApiError } from '../lib/api';
import { type AccountLink as Link, readLink, setPasswordFromLink } from '../lib/accounts';
import { Button } from '../ui/Button';
import { Logo } from '../ui/misc';
import { AuthFrame } from './SignedOut';

const field = 'h-11 w-full rounded-lg border border-line bg-panel px-3 text-[15px] outline-none focus:border-brand/50';

/** The token from the address's fragment (never sent to a server by the browser); an effect then removes it. */
function readToken() {
  const token = window.location.hash.slice(1);
  return /^[A-Za-z0-9_-]{43}$/.test(token) ? token : null;
}

/**
 * An emailed setup or reset link (WRMM-134): choose a password, then the console's second step (setting up a second
 * factor, for a new account) signs the person in. No sign-in needed to open it.
 */
export function AccountLinkPage() {
  // Read without side effects (StrictMode runs initializers twice in development), then taken out of the address.
  const [token] = useState(readToken);
  useEffect(() => {
    if (window.location.hash) window.history.replaceState(window.history.state, '', window.location.pathname);
  }, []);
  const [link, setLink] = useState<Link | null | 'invalid'>(null);
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!token) { setLink('invalid'); return; }
    readLink(token).then(setLink, () => setLink('invalid'));
  }, [token]);
  if (link === null) return <AuthFrame><p className="text-center text-ink-2">Checking your link…</p></AuthFrame>;
  if (link === 'invalid') {
    return (
      <AuthFrame>
        <div role="alert" className="rounded-2xl border border-line bg-panel p-8 shadow-pop">
          <Logo className="size-10" />
          <h1 className="mt-6 text-[22px] font-semibold tracking-tight">This link doesn’t work</h1>
          <p className="mt-2 text-ink-2">It was used already, has expired, or a newer one replaced it. Ask for a new password reset from the sign-in page, or ask your IT provider to send the setup email again.</p>
          <a href="/" className="mt-5 inline-block text-[13.5px] text-brand underline-offset-2 hover:underline">Go to sign-in</a>
        </div>
      </AuthFrame>
    );
  }
  const problem = password ? passwordProblem(password, link.email) : null;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (problem || password !== again) return;
    setBusy(true); setError(null);
    try {
      await setPasswordFromLink(token!, password);
      window.location.assign('/?signin=second-factor'); // The second step is waiting there, even over another session.
    } catch (failure) {
      setBusy(false);
      setError(failure instanceof ApiError && failure.status === 401 ? 'This link stopped working. Ask for a new one.' : 'That didn’t work. Check the password and try again.');
    }
  };
  return (
    <AuthFrame>
      <form onSubmit={e => void submit(e)} className="rounded-2xl border border-line bg-panel/95 p-8 shadow-pop backdrop-blur">
        <Logo className="size-10" />
        <h1 className="mt-6 flex items-center gap-2 text-[22px] font-semibold tracking-tight"><KeyRound className="size-5 text-brand" />
          {link.purpose === 'setup' ? 'Set up your account' : 'Choose a new password'}</h1>
        <p className="mt-1.5 text-ink-2">{link.name ? `${link.name}, ` : ''}for <span className="font-medium text-ink">{link.email}</span>.
          {link.purpose === 'setup' ? ' Next you’ll set up a second factor, which every sign-in asks for.' : ' Your second factor stays as it is.'}</p>
        <label className="mt-5 block text-[13px] font-medium" htmlFor="new-password">New password</label>
        <input id="new-password" type="password" autoComplete="new-password" className={`${field} mt-1`} value={password} onChange={e => setPassword(e.target.value)} autoFocus />
        <p className={`mt-1 text-[12.5px] ${problem ? 'text-attention' : 'text-ink-3'}`}>{problem ?? `At least ${PASSWORD_MIN} characters. A few words you’ll remember work well.`}</p>
        <label className="mt-3 block text-[13px] font-medium" htmlFor="again-password">The same again</label>
        <input id="again-password" type="password" autoComplete="new-password" className={`${field} mt-1`} value={again} onChange={e => setAgain(e.target.value)} />
        {again && password !== again && <p className="mt-1 text-[12.5px] text-attention">The two don’t match.</p>}
        {error && <p role="alert" className="mt-3 text-[13px] text-attention">{error}</p>}
        <Button variant="primary" type="submit" className="mt-5 h-11 w-full justify-center" disabled={busy || !password || !!problem || password !== again}>
          {busy ? 'Saving…' : 'Continue'}</Button>
      </form>
    </AuthFrame>
  );
}
